import { useEffect, useRef } from 'react';

const MAX_ERRORS = 10;

const normalizeError = (error, source = 'console') => {
  if (!error) {
    return { source, message: 'Unknown error', timestamp: new Date().toISOString() };
  }

  if (error instanceof Error) {
    return {
      source,
      message: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString()
    };
  }

  if (typeof error === 'string') {
    return { source, message: error, timestamp: new Date().toISOString() };
  }

  try {
    return {
      source,
      message: JSON.stringify(error),
      timestamp: new Date().toISOString()
    };
  } catch (jsonError) {
    return {
      source,
      message: 'Unserializable error object',
      timestamp: new Date().toISOString()
    };
  }
};

const pushError = (bufferRef, entry) => {
  bufferRef.current.push(entry);
  if (bufferRef.current.length > MAX_ERRORS) {
    bufferRef.current.shift();
  }
};

const useConsoleErrors = () => {
  const errorBufferRef = useRef([]);
  const originalConsoleErrorRef = useRef(null);

  useEffect(() => {
    originalConsoleErrorRef.current = console.error;

    console.error = (...args) => {
      originalConsoleErrorRef.current(...args);
      args.forEach((arg) => pushError(errorBufferRef, normalizeError(arg, 'console')));
    };

    const handleWindowError = (event) => {
      pushError(errorBufferRef, normalizeError(event.error || event.message, 'window'));
    };

    const handleUnhandledRejection = (event) => {
      pushError(errorBufferRef, normalizeError(event.reason, 'unhandledrejection'));
    };

    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      if (originalConsoleErrorRef.current) {
        console.error = originalConsoleErrorRef.current;
      }
      window.removeEventListener('error', handleWindowError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  const getRecentErrors = () => {
    return [...errorBufferRef.current];
  };

  return { getRecentErrors };
};

export default useConsoleErrors;
